import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  MIN_SERVING_SIZE,
  usePlanServiceCustomerPlansView,
  usePlanServingSize,
  usePostCheckoutPricingInfo,
} from '@hungryroot/plan';
import { PDPModal } from '@hungryroot/rdp';
import {
  useGetAccountSettings,
  usePricingServiceCustomerPlansPrice,
} from '@hungryroot/rest-hooks';
import {
  ACCOUNT_SETTINGS_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  PLAN_ROUTE,
} from '@hungryroot/routes';
import { buttonClickedEvent, productInteraction } from '@hungryroot/segment';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';

import { useCanSwitchToBiweeklyDeliveries } from '../../../hooks/useCanSwitchToBiweeklyDeliveries';
import { useReadyToEatProducts } from '../../../hooks/useReadyToEatProducts';
import { buildOccasionLineItems } from '../../../utils/buildOccasionLineItems';
import { PlanSummaryCard } from '../../shared/PlanSummaryCard';
import { ProductCard } from '../MorePreparedMealsDeflection/components/ProductCard';
import { SkeletonCard } from '../MorePreparedMealsDeflection/components/SkeletonCard';
import styles from './WantSmallerDeliveriesThanSmallestPlanDeflection.module.css';

const PAGE_POSITION =
  'cancel_deflection_want_smaller_deliveries_than_smallest_plan';
const SEE_MORE_URL = `${EDIT_DELIVERY_GROCERIES_ROUTE}?is_ready_to_eat=1`;
const PRODUCT_PREVIEW_COUNT = 6;
const SKELETON_COUNT = 6;
const BIWEEKLY_FREQUENCY_DAYS = 14;

function formatServingSize(servingSize: number | undefined) {
  if (servingSize == null) {
    return undefined;
  }
  return `${servingSize} ${
    servingSize === 1 ? 'serving' : 'servings'
  } per recipe`;
}

export function WantSmallerDeliveriesThanSmallestPlanDeflection() {
  const history = useHistory();

  // Section 1 — smaller serving size
  const { servingSize, isLoading: isServingSizeLoading } = usePlanServingSize();
  const showSmallerServingsSection =
    !isServingSizeLoading &&
    servingSize !== undefined &&
    servingSize > MIN_SERVING_SIZE;

  // Section 2 — prepared meals carousel
  const { data: readyToEatData, isLoading: isProductsLoading } =
    useReadyToEatProducts();
  const products = readyToEatData?.products ?? [];
  const previewedProducts = products.slice(0, PRODUCT_PREVIEW_COUNT);
  const showProductsSection = isProductsLoading || previewedProducts.length > 0;
  const [pdpProductId, setPdpProductId] = useState<number | null>(null);

  // Section 3 — bi-weekly frequency (same Exp 178 gate as account settings)
  const { data: accountSettings } = useGetAccountSettings();
  const isOnBiWeekly = accountSettings?.frequency === BIWEEKLY_FREQUENCY_DAYS;
  const canSwitchToBiweekly = useCanSwitchToBiweeklyDeliveries();
  const showFrequencySection =
    accountSettings !== undefined && !isOnBiWeekly && canSwitchToBiweekly;

  // Section 4 — customize plan
  const { data: currentPlan, isLoading: isCurrentPlanLoading } =
    usePlanServiceCustomerPlansView({ is_staged: 0 });
  const { data: currentPricing, isLoading: isCurrentPricingLoading } =
    usePostCheckoutPricingInfo({ is_staged: 0 });
  const { data: currentPriceData, isLoading: isCurrentPriceLoading } =
    usePricingServiceCustomerPlansPrice({ is_staged: 0 });

  const currentPricedContainers = useMemo(
    () => currentPriceData?.priced_containers ?? [],
    [currentPriceData]
  );
  const currentItems = useMemo(() => {
    if (!currentPlan) {
      return [];
    }
    return buildOccasionLineItems({
      components: currentPlan.plan_components,
      pricedContainers: currentPricedContainers,
    });
  }, [currentPlan, currentPricedContainers]);

  const currentTotal = currentPricing
    ? Number(currentPricing.plan_pricing.total)
    : null;
  const currentCredits = currentPricing
    ? Number(currentPricing.plan_credits)
    : null;
  const isCurrentLoading =
    isCurrentPlanLoading || isCurrentPricingLoading || isCurrentPriceLoading;

  return (
    <div className={styles.container}>
      <Heading variant="Hero" className={styles.title}>
        Ways to reduce your box size
      </Heading>
      <Paragraph className={styles.intro}>
        Here are a few options that can help shrink your delivery without
        canceling your subscription.
      </Paragraph>

      {showSmallerServingsSection ? (
        <section
          className={styles.section}
          data-testid="cancellation-deflection-smaller-servings"
        >
          <Heading variant="H4" className={styles['section-title']}>
            Try a smaller serving size
          </Heading>
          <Paragraph className={styles['section-body']}>
            You&apos;re currently on{' '}
            <strong>{formatServingSize(servingSize)}</strong>. Going down to a
            smaller serving size can help reduce the amount of food in each
            delivery.
          </Paragraph>
          <div className={styles['cta-container']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'Update serving size',
                  order_id: null,
                  page_position: PAGE_POSITION,
                });
                history.push(PLAN_ROUTE);
              }}
              style="default"
              size="medium"
              prominence="secondary"
              textPrimary="Update serving size"
              testid="cancellation-deflection-go-to-plan"
            />
          </div>
        </section>
      ) : null}

      {showProductsSection ? (
        <section
          className={styles.section}
          data-testid="cancellation-deflection-prepared-meals"
        >
          <Heading variant="H4" className={styles['section-title']}>
            Add 1-serving prepared meals
          </Heading>
          <Paragraph className={styles['section-body']}>
            Heat-and-eat and ready-to-eat meals come in single servings — a
            convenient way to round out a smaller delivery.
          </Paragraph>
          <div className={styles['product-grid']}>
            {isProductsLoading
              ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))
              : previewedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => {
                      productInteraction({
                        productId: product.id,
                        productName: product.name,
                        actionType: 'clicked',
                        location: 'card',
                      });
                      setPdpProductId(product.id);
                    }}
                  />
                ))}
          </div>
          <div className={styles['cta-container']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'See all prepared meals',
                  order_id: null,
                  page_position: PAGE_POSITION,
                });
                history.push(SEE_MORE_URL);
              }}
              style="default"
              size="medium"
              prominence="secondary"
              textPrimary="See all prepared meals"
              testid="cancellation-deflection-see-all-meals"
            />
          </div>
        </section>
      ) : null}

      {showFrequencySection ? (
        <section
          className={styles.section}
          data-testid="cancellation-deflection-biweekly"
        >
          <Heading variant="H4" className={styles['section-title']}>
            Switch to bi-weekly deliveries
          </Heading>
          <Paragraph className={styles['section-body']}>
            Receive your deliveries every other week instead of every week so
            food has more time to last between boxes.
          </Paragraph>
          <div className={styles['cta-container']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'Update delivery frequency',
                  order_id: null,
                  page_position: PAGE_POSITION,
                });
                history.push(ACCOUNT_SETTINGS_ROUTE);
              }}
              style="default"
              size="medium"
              prominence="secondary"
              textPrimary="Update delivery frequency"
              testid="cancellation-deflection-go-to-account-settings"
            />
          </div>
        </section>
      ) : null}

      <section
        className={styles.section}
        data-testid="cancellation-deflection-customize-plan"
      >
        <Heading variant="H4" className={styles['section-title']}>
          Customize your plan
        </Heading>
        <Paragraph className={styles['section-body']}>
          Your current plan includes the options below. Adjust your breakfast,
          lunch, dinner, snacks, and sweets to better fit your needs.
        </Paragraph>
        <PlanSummaryCard
          testid="cancellation-deflection-current-plan"
          title="Your current plan"
          subtitle={formatServingSize(servingSize)}
          subtitleVariant="info"
          items={currentItems}
          total={currentTotal}
          credits={currentCredits}
          isLoading={isCurrentLoading}
          ctaLabel="Customize my plan"
          onSelect={() => {
            buttonClickedEvent({
              button_name: 'Customize my plan',
              order_id: null,
              page_position: PAGE_POSITION,
            });
            history.push(PLAN_ROUTE);
          }}
        />
      </section>

      {pdpProductId !== null && (
        <PDPModal
          id={pdpProductId}
          onDismiss={() => setPdpProductId(null)}
          stacked={false}
          footerButton={<></>}
          enableFoodProfileNeverWarnings={true}
        />
      )}
    </div>
  );
}
